import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  UserProfile,
  UserProfileResponse,
  Department,
  Timezone,
} from '../../store/entities/models/user-profile.model';
import { ConfigService } from './config.service';

interface ProfileApiResponse {
  data: UserProfileResponse;
}

interface DepartmentsApiResponse {
  data: { id: number; name: string }[];
}

interface TimezonesApiResponse {
  data: { name: string; offset: string }[];
}

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private http = inject(HttpClient);
  private config = inject(ConfigService);

  private get apiUrl(): string {
    return this.config.api.host;
  }

  getProfile(): Observable<UserProfile> {
    return this.http.get<ProfileApiResponse>(`${this.apiUrl}/profile`).pipe(
      map((response) => this.mapProfileResponse(response.data))
    );
  }

  updateProfile(data: Partial<UserProfile>): Observable<UserProfile> {
    const payload = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      timezone: data.timezone,
      department_id: data.department?.id || null,
      phone_number: data.phoneNumber || null,
      country_code: data.countryCode || null,
    };

    return this.http.post<ProfileApiResponse>(`${this.apiUrl}/profile/update`, payload).pipe(
      map((response) => this.mapProfileResponse(response.data))
    );
  }

  getDepartments(): Observable<Department[]> {
    return this.http.get<DepartmentsApiResponse>(`${this.apiUrl}/departments`).pipe(
      map((response) => response.data.map((dept) => ({
        id: dept.id,
        name: dept.name,
      })))
    );
  }

  getTimezones(): Observable<Timezone[]> {
    return this.http.get<TimezonesApiResponse>(`${this.apiUrl}/timezones`).pipe(
      map((response) => {
        const timezoneArray = Array.isArray(response) ? response : response.data;

        return timezoneArray.map((tz: { name: string; offset: string }) => ({
          label: `${tz.name} ${tz.offset}`,
          value: tz.name,
          offset: tz.offset,
        }));
      })
    );
  }

  regenerateToken(refreshToken: string): Observable<{ refreshToken: string }> {
    return this.http
      .post<{ data?: { token?: string }; token?: string }>(
        `${this.apiUrl}/update-refresh`,
        { refresh_token: refreshToken }
      )
      .pipe(
        map((response) => ({
          refreshToken: response?.token ?? '',
        }))
      );
  }

  uploadPhoto(file: File): Observable<{ photo: string }> {
    const formData = new FormData();

    formData.append('photo', file);

    return this.http.post<{ photo: string }>(`${this.apiUrl}/profile/update/photo`, formData).pipe(
      map((response) => ({
        photo: response.photo,
      }))
    );
  }

  deletePhoto(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/profile/delete/photo`, {});
  }

  private mapProfileResponse(response: UserProfileResponse): UserProfile {
    return {
      uuid: response.uuid,
      email: response.email,
      fullName: response.full_name,
      firstName: response.first_name,
      lastName: response.last_name,
      timezone: response.timezone,
      defaultDomainUuid: response.default_domain_uuid,
      defaultDomainRedirectType: response.default_domain_redirect_type,
      photo: response.photo,
      department: response.department ? {
        id: response.department.id,
        name: response.department.name,
      } : null,
      refreshToken: response.refresh_token,
      enablePasswordEdit: response.enable_password_edit,
      phoneNumber: response.phone_number,
      countryCode: response.country_code,
      linksCount: response.links_count,
      tagsCount: response.tags_count,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
    };
  }
}
